import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_DASHBOARD_LEADS, GET_DASHBOARD_LEAD_COUNTS } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface UseDashboardLeadsGQLParams {
  dateRange: DateRange;
  isModalOpen?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  pageSize?: number;
  leadSources?: string[];
  search?: string;
}

const PAGE_SIZE = 25;

interface GQLDashboardContact {
  contactId?: unknown;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  leadSource?: string;
  leadSourceSecondary?: string | null;
  intentLevel?: string;
  leadIntent?: string | null;
  optInDate?: string;
  optInDesc?: string;
  createdAt?: string;
  valuationRequested?: boolean;
  claimedLead?: string | null;
  claimedLeadSource?: string | null;
  assignedAgentId?: string | null;
  assignedPondId?: string | null;
  assignedAgent?: {
    agentId?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    photoUrl?: string;
  } | null;
}

type LeadsQueryData = { contacts?: { contacts?: GQLDashboardContact[]; total?: number } };

type LeadCountsData = {
  emailLeads?: { total?: number };
  organicLeads?: { total?: number };
  ppcLeads?: { total?: number };
};

const SORT_FIELD_MAP: Record<string, string> = {
  fullName: 'fullName',
  dateTime: 'optInDate',
  optInDate: 'optInDate',
  intent: 'intentLevel',
  leadSource: 'leadSource',
  claimedLead: 'assignedAgent',
  assignedAgent: 'assignedAgent',
  email: 'email',
  createdAt: 'createdAt',
};

const mapSortField = (field: string) => SORT_FIELD_MAP[field] ?? 'optInDate';

export const useDashboardLeadsGQL = ({
  dateRange,
  isModalOpen = false,
  sortField = 'dateTime',
  sortDirection = 'desc',
  pageSize = PAGE_SIZE,
  leadSources,
  search,
}: UseDashboardLeadsGQLParams) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';
  const [currentPage, setCurrentPage] = useState(0);

  // Reset to page 0 when page size or any filter (source / search) changes
  useEffect(() => {
    setCurrentPage(0);
  }, [pageSize, leadSources, search]);

  const offset = currentPage * pageSize;
  const optInDateFrom = dateRange.from?.toISOString() ?? undefined;
  const optInDateTo = dateRange.to?.toISOString() ?? undefined;

  // Modal data: full contact list, only fetched when modal is open
  const { data: countsData, loading: countsLoading } = useQuery<LeadCountsData>(GET_DASHBOARD_LEAD_COUNTS, {
    variables: { organizationId: orgId, optInDateFrom, optInDateTo },
    skip: true,
    fetchPolicy: 'cache-first',
  });

  // Modal data: full contact list, only fetched when modal is open
  const { data, loading, refetch } = useQuery<LeadsQueryData>(GET_DASHBOARD_LEADS, {
    variables: {
      organizationId: orgId,
      limit: pageSize,
      offset,
      optInDateFrom,
      optInDateTo,
      leadSources: leadSources?.length ? leadSources : undefined,
      search: search?.trim() ? search.trim() : undefined,
      sortBy: mapSortField(sortField),
      sortOrder: sortDirection.toUpperCase(),
    },
    skip: !isModalOpen || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const contacts = data?.contacts?.contacts ?? [];
  const total = data?.contacts?.total ?? 0;

  const leads = contacts.map((c: GQLDashboardContact) => ({
    id: c.contactId,
    fullName: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim(),
    phoneNumber: c.phone ?? '',
    email: c.email ?? '',
    // Do not silently fall back to createdAt; dashboard should only show a real opt-in date here.
    dateTime: c.optInDate ?? '',
    leadSource: c.leadSource ?? '',
    leadSourceSecondary: c.leadSourceSecondary ?? '',
    conversionSource: c.optInDesc ?? '',
    intent: (c.leadIntent ?? c.intentLevel) ?? '',
    valuationRequested: c.valuationRequested ? [{ address: '', requestedAt: '' }] : undefined,
    claimedLead: (() => {
      if (!c.claimedLead) return undefined;
      let name = c.claimedLead;
      let phone = '';
      try {
        const parsed = JSON.parse(c.claimedLead);
        name = parsed?.name || parsed?.agentName || c.claimedLead;
        phone = parsed?.phoneNumber || '';
      } catch {
        // plain string — use as-is
      }
      return {
        name,
        phoneNumber: phone || c.assignedAgent?.phone || '',
        id: undefined,
        email: c.assignedAgent?.email ?? '',
      };
    })(),
    claimedLeadSource: c.claimedLeadSource ?? null,
    assignedAgentId: c.assignedAgentId ?? null,
    assignedPondId: c.assignedPondId ?? null,
    assignedAgent: c.assignedAgent
      ? {
          agentId: c.assignedAgent.agentId ?? '',
          firstName: c.assignedAgent.firstName ?? '',
          lastName: c.assignedAgent.lastName ?? '',
          email: c.assignedAgent.email ?? null,
          phone: c.assignedAgent.phone ?? null,
          photoUrl: c.assignedAgent.photoUrl ?? null,
        }
      : null,
  }));

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const refresh = useCallback(async () => {
    setCurrentPage(0);
    await refetch({ offset: 0 });
  }, [refetch]);

  return {
    data: leads,
    isLoading: loading,
    countsLoading,
    error: null,
    totalCount: total,
    leadCounts: {
      email: countsData?.emailLeads?.total ?? 0,
      organic: countsData?.organicLeads?.total ?? 0,
      ppc: countsData?.ppcLeads?.total ?? 0,
      get all() { return this.email + this.organic + this.ppc; },
    },
    currentPage,
    pageSize,
    goToPage,
    refresh,
  };
};
